import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ProfessionalPixelStudio from './ProfessionalPixelStudio';

// Importação massiva de ícones para manter a interface rica
import { 
  Users, Eye, Briefcase, Building2, TrendingUp, Plus, LogOut, 
  MapPin, CheckCircle, Trash2, Activity, Edit, Link as LinkIcon,
  AlignLeft, DollarSign, Globe, Smartphone, Mail, FileText,
  Sun, Moon, Image as ImageIcon, Tag, Video, FileUp, Link2, Upload,
  Info, AlertCircle, Sparkles, Scissors, RotateCw, SlidersHorizontal, 
  Maximize, LayoutDashboard, Search, Filter, ChevronRight, RefreshCcw,
  ShieldCheck, Clock, Download, Share2 , X } from 'lucide-react';
import { jobsAPI, statsAPI, uploadAPI, BACKEND_URL } from '../lib/api';
// UI Components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';

// API Hooks & Utils

import { toast } from 'sonner';



/**
 * CONFIGURAÇÕES E CONSTANTES GLOBAIS
 * Centralizadas para fácil manutenção e expansão do sistema.
 */// Definição da função MEDIA para evitar o erro de ReferenceError
const MEDIA = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `https://backend-empregos.onrender.com/${cleanPath}`;
};


const LOGO_URL = 'https://customer-assets.emergentagent.com/job_empregos-ro/artifacts/8ffadfy5_c2ea4279-1513-4151-8935-c05559291e0c.png';

const CITIES = [
  'Porto Velho', 'Ji-Paraná', 'Ariquemes', 'Vilhena', 'Cacoal', 
  'Rolim de Moura', 'Jaru', 'Guajará-Mirim', 'Ouro Preto do Oeste', 'Pimenta Bueno', 'Outras'
];

const AREAS = [
  'TI e Programação', 'Comércio e Vendas', 'Saúde', 'Indústria', 'Administrativo', 
  'Educação', 'Agronegócio', 'Construção Civil', 'Marketing', 'Jurídico', 'Outros'
];

const CATEGORIES = [
  { id: 'vaga', label: 'Vaga de Emprego (Padrão)', color: 'teal' },
  { id: 'concurso', label: 'Concurso Público', color: 'blue' },
  { id: 'estagio', label: 'Estágio / Jovem Aprendiz', color: 'indigo' },
  { id: 'freelance', label: 'Freelancer / Bico', color: 'purple' },
  { id: 'curso', label: 'Curso / Treinamento', color: 'amber' },
  { id: 'eleicao', label: 'Eleição / Edital', color: 'rose' },
  { id: 'noticia', label: 'Notícia / Informativo', color: 'orange' },
  { id: 'voluntario', label: 'Trabalho Voluntário', color: 'green' }
];

const INFO_CATEGORIES = ['noticia', 'eleicao', 'curso'];

// Configuração avançada do editor Rich Text (ReactQuill)
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link', 'image', 'video'],
    ['clean']
  ],
};

/**
 * ESTILOS REUTILIZÁVEIS (Tailwind)
 * Padronização visual para inputs e containers
 */const getOptimizedBlob = async (dataUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = dataUrl;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      // Força o tamanho ideal para o WhatsApp
      canvas.width = 1200;
      canvas.height = 630;
      ctx.drawImage(img, 0, 0, 1200, 630);
      // Qualidade 0.7 para garantir que fique abaixo de 300KB
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.7);
    };
  });
};

const modernInputClasses = "w-full bg-white dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-xl p-4 text-lg text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 transition-all outline-none";
const modernLabelClasses = "block text-sm font-bold text-slate-600 dark:text-slate-300 mb-2 uppercase tracking-wider pl-1";
const sidebarCardClasses = "bg-white dark:bg-slate-900/80 p-6 rounded-2xl border border-slate-200 dark:border-slate-800/80 space-y-5 shadow-sm dark:shadow-lg backdrop-blur-sm";

/**
 * COMPONENTE: StatCard
 * Exibe métricas de desempenho com design moderno e gradientes
 */
const StatCard = ({ icon: Icon, value, label, trend, color, isDark }) => (
  <div className={`relative group overflow-hidden rounded-2xl p-6 transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${isDark ? color : 'bg-white border border-slate-100 shadow-xl shadow-slate-200/50'}`}>
    <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-150 transition-transform duration-700">
        <Icon size={120} />
    </div>
    <div className="flex items-start justify-between relative z-10">
      <div>
        <p className={`text-xs mb-1 font-bold uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-slate-500'}`}>{label}</p>
        <p className={`text-4xl font-black font-heading tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>{value}</p>
        {trend && (
          <div className={`flex items-center gap-1.5 mt-3 py-1 px-2.5 rounded-full w-fit text-[10px] font-bold ${isDark ? 'text-white bg-white/20' : 'text-emerald-700 bg-emerald-50 border border-emerald-100'}`}>
            <TrendingUp className="w-3 h-3" />
            {trend}
          </div>
        )}
      </div>
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-inner ${isDark ? 'bg-white/20' : 'bg-slate-50 border border-slate-100'}`}>
        <Icon className={`w-7 h-7 ${isDark ? 'text-white' : 'text-teal-600'}`} />
      </div>
    </div>
  </div>
);

/**
 * PÁGINA PRINCIPAL: AdminDashboardPage
 * Dashboard administrativo robusto para gestão de portal de empregos e notícias.
 */
const AdminDashboardPage = () => {
  const navigate = useNavigate();
  // ✅ Adiciona esta função dentro de "const AdminDashboardPage = () => {"
  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Tem certeza? Esta ação não pode ser desfeita.')) return;
    
    const toastId = toast.loading('Removendo vaga...');
    try {
      // 1. Faz a chamada ao backend Python através do jobsAPI definido em api.js
      await jobsAPI.delete(jobId);
      
      toast.success('Vaga removida com sucesso!', { id: toastId });
      
      // 2. Atualiza o estado local para a vaga desaparecer da lista sem refresh
      if (typeof setJobs === 'function') {
        setJobs(prev => prev.filter(j => j.id !== jobId));
      }
      
      // 3. Recarrega as estatísticas e dados
      if (typeof loadData === 'function') loadData();
      
    } catch (err) {
      console.error("Erro ao deletar:", err);
      toast.error('Erro ao excluir a vaga no servidor.', { id: toastId });
    }
  };
  
  // -- ESTADOS DE INTERFACE --
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') !== 'light');
  const [loading, setLoading] = useState(true);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
 const [imageRatio, setImageRatio] = useState('16:9');
  // -- ESTADOS DE DADOS --
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [totalViews, setTotalViews] = useState(0);
  
  // -- ESTADOS DE FORMULÁRIO --
  const [isEditing, setIsEditing] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [salaryType, setSalaryType] = useState('fixed');
  const [imageFilters, setImageFilters] = useState({
    brightness: 100, contrast: 100, saturate: 100, grayscale: 0, rotate: 0, sepia: 0
  });
const [showAdvancedEditor, setShowAdvancedEditor] = useState(false);
const [imageToEdit, setImageToEdit] = useState(null);
  const [jobForm, setJobForm] = useState({
    title: '', company_name: '', city: 'Porto Velho', area: 'Outros', description: '',
    requirements: '', benefits: '', salary_min: '', salary_max: '',
    modality: 'presencial', contact_email: '', contact_phone: '',
    link: '', image_url: '', category: 'vaga',
    video_url: '', document_url: '', slug: '', image_display_mode: 'cover'
  });

  const isInfoType = useMemo(() => INFO_CATEGORIES.includes(jobForm.category), [jobForm.category]);

  // -- EFEITOS DE INICIALIZAÇÃO --
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) root.classList.add('dark');
    else root.classList.remove('dark');
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin');
    else loadData();
  }, [navigate]);

  // -- FUNÇÕES DE DADOS --
  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, jobsRes] = await Promise.all([
        statsAPI.get(),
        jobsAPI.getAll({ limit: 500 }) 
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setTotalViews(statsRes.data.total_views || 0);
    } catch (err) {
      toast.error('Erro na sincronização de dados.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Sessão encerrada com segurança');
    navigate('/admin');
  };
  
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove acentos
    .replace(/[^a-z0-9\s-]/g, "") // Remove símbolos
    .trim()
    .replace(/\s+/g, "-"); // Troca espaços por hífens
};

// No seu Input de Título, deixe assim:
<Input 
  placeholder="Título da Vaga"
  value={jobForm.title}
  onChange={(e) => {
    const title = e.target.value;
    // Aqui está o segredo: ele atualiza o título e o slug ao mesmo tempo
    setJobForm({ 
      ...jobForm, 
      title, 
      slug: generateSlug(title) 
    });
  }}
/>
  // -- GERENCIAMENTO DE MODAL E FORMULÁRIO --
  const handleOpenCreateModal = () => {
    setIsEditing(false);
    setCurrentJobId(null);
    setJobForm({
      title: '', company_name: '', city: 'Porto Velho', area: 'Outros', description: '',
      requirements: '', benefits: '', salary_min: '', salary_max: '',
      modality: 'presencial', contact_email: '', contact_phone: '',
      link: '', image_url: '', category: 'vaga',
      video_url: '', document_url: '', slug: '', image_display_mode: 'cover'
    });
    setShowJobModal(true);
  };

  const handleOpenEditModal = (job) => {
    setIsEditing(true);
    setCurrentJobId(job.id);
    let type = 'fixed';
    if (!job.salary_min && !job.salary_max) type = 'negotiable';
    else if (parseFloat(job.salary_min) !== parseFloat(job.salary_max)) type = 'range';
    
    setSalaryType(type);
    setJobForm({ ...job });
    setShowJobModal(true);
  };

// -- UPLOADS E PROCESSAMENTO DE IMAGEM --
const handleImageUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const toastId = toast.loading("Fazendo upload da imagem...");
  try {
    // 1. Enviamos APENAS o arquivo 'file'. 
    // O seu uploadAPI.upload já vai embrulhar ele em um FormData automaticamente.
    const res = await uploadAPI.upload(file);
    
    // 2. Pegamos o caminho que o Python retornou (ex: /uploads/nome-da-foto.jpg)
    const relativePath = res.data.url;
    
    // 3. Montamos a URL completa usando o BACKEND_URL que definimos no api.js
    // Isso garante que funcione tanto no localhost quanto no Render
    const finalUrl = relativePath.startsWith('http') 
      ? relativePath 
      : `${BACKEND_URL}${relativePath.startsWith('/') ? '' : '/'}${relativePath}`;

    // 4. Salvamos no formulário para ser enviado ao banco de dados
    setJobForm(prev => ({ ...prev, image_url: finalUrl }));
    
    toast.success("Imagem processada com sucesso!", { id: toastId });
  } catch (err) {
    console.error("Erro no upload:", err);
    toast.error("Falha ao subir imagem para o servidor.", { id: toastId });
  }
};
const handleFileUpload = async (e, field) => { 
  const file = e.target.files[0];
  if (!file) return;

  const toastId = toast.loading(`Enviando ${file.name}...`);
  try {
    // ✅ CORREÇÃO: Passamos apenas o 'file'. O api.js monta o FormData.
    const res = await uploadAPI.upload(file); 
    
    // O backend retorna a URL, salvamos no campo (ex: document_url ou video_url)
    setJobForm(prev => ({ ...prev, [field]: res.data.url }));
    toast.success("Arquivo pronto!", { id: toastId });
  } catch (err) {
    console.error(err);
    toast.error("Falha no envio do arquivo", { id: toastId });
  }
};
  const getImageStyle = useCallback(() => ({
    filter: `brightness(${imageFilters.brightness}%) contrast(${imageFilters.contrast}%) saturate(${imageFilters.saturate}%) grayscale(${imageFilters.grayscale}%) sepia(${imageFilters.sepia}%)`,
    transform: `rotate(${imageFilters.rotate}deg)`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  }), [imageFilters]);

  // -- PERSISTÊNCIA --
 const handleSaveJob = async (e) => {
  if (e?.preventDefault) e.preventDefault();

  if (!jobForm.title || !jobForm.company_name || !jobForm.description) {
    return toast.error('Preencha os campos essenciais!');
  }

  try {
    // ✅ Garantimos que valores numéricos sejam números reais e não strings
    const data = {
      ...jobForm,
      slug: jobForm.slug || (jobForm.title ? generateSlug(jobForm.title) : ""),
      salary_min: (isInfoType || salaryType === 'negotiable') ? 0 : Number(jobForm.salary_min || 0),
      salary_max: (isInfoType || salaryType === 'negotiable') ? 0 : (salaryType === 'fixed' ? Number(jobForm.salary_min || 0) : Number(jobForm.salary_max || 0)),
    };

    // ✅ Chamada para o backend Python
    if (isEditing) {
      await jobsAPI.update(currentJobId, data);
    } else {
      // Se for admin usando o Dashboard, talvez você queira usar jobsAPI.create(data)
      await jobsAPI.createPublic(data); 
    }
    
    toast.success(isEditing ? 'Atualizado!' : 'Publicado!');
    setShowJobModal(false);
    loadData();
  } catch (err) {
    // Dica: olhe o console para ver se o erro é 422 (erro de validação do Python)
    console.error("Erro ao salvar:", err.response?.data);
    toast.error(err.response?.data?.detail || 'Erro ao salvar.');
  }
};

  // -- FILTRAGEM --
  const filteredJobs = jobs.filter(j => 
    j.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    j.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !stats) {
    return (
      
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center gap-4">
        <div className="relative">
            <div className="animate-spin w-16 h-16 border-4 border-teal-500/20 border-t-teal-500 rounded-full" />
            <div className="absolute inset-0 flex items-center justify-center">
                <Activity className="w-6 h-6 text-teal-500 animate-pulse" />
            </div>
        </div>
        <p className="text-slate-500 font-bold animate-pulse uppercase tracking-widest text-xs">Carregando Ecossistema...</p>
      </div>
    );
  }

return (
    

      <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#020617] transition-colors duration-500 pb-20 font-sans">
      
      {/* BARRA SUPERIOR (HEADER) */}
      <header className="bg-white/70 dark:bg-slate-900/70 border-b border-slate-200 dark:border-slate-800/60 sticky top-0 z-50 backdrop-blur-2xl">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                <img src={LOGO_URL} alt="Logo" className="h-11 relative cursor-pointer" onClick={() => navigate('/dashboard')} />
              </div>
              <Separator orientation="vertical" className="h-8 hidden md:block" />
              <div className="hidden md:block">
                <h1 className="font-black text-xl text-slate-800 dark:text-white leading-tight">Painel de Controle</h1>
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />
                    <p className="text-[10px] text-teal-600 font-black uppercase tracking-[0.2em]">Live: Portal Rondônia</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative hidden lg:block mr-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input 
                  type="text" 
                  placeholder="Pesquisar registros..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-slate-100 dark:bg-slate-800 border-none rounded-xl py-2 pl-10 pr-4 text-sm w-64 focus:ring-2 focus:ring-teal-500/50 transition-all"
                />
              </div>
              
              <button 
                onClick={() => setIsDarkMode(!isDarkMode)}
                className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:scale-110 active:scale-95 transition-all shadow-sm"
              >
  



  
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <Button onClick={handleOpenCreateModal} className="bg-teal-600 hover:bg-teal-500 text-white rounded-2xl px-6 h-12 shadow-lg shadow-teal-500/25 font-bold transition-all hover:gap-4 active:scale-95">
                <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Criar Conteúdo</span>
              </Button>
              
              <Button variant="ghost" onClick={handleLogout} className="text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-2xl w-12 h-12 p-0">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-10">
        
       {/* DASHBOARD METRICS */}
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
  
  {/* Publicações Ativas */}
  <StatCard 
    isDark={isDarkMode} 
    icon={Briefcase} 
    value={stats?.total_jobs || 0} 
    label="Publicações Ativas" 
    color="bg-gradient-to-br from-slate-800 to-slate-950 text-white" 
    // trend="+14 novos hoje"  <-- Removido para não mentir os dados
  />

  {/* Fontes & Empresas */}
 <StatCard 
  isDark={isDarkMode} 
  icon={Building2} 
  value={stats?.total_jobs || 0} // Se quiser mostrar o total de posts, use total_jobs
  label="Fontes & Empresas" 
  color="bg-gradient-to-br from-teal-500 to-teal-700 text-white" 
/>

  {/* Impacto Digital */}
  <StatCard 
    isDark={isDarkMode} 
    icon={Eye} 
    value={totalViews || 0} // Removi o toLocaleString() momentaneamente para garantir o 0
    label="Impacto Digital" 
    color="bg-gradient-to-br from-indigo-500 to-blue-700 text-white" 
    trend="Visualizações" // Mudei de "Audiência Real" para algo mais neutro
  />

  {/* Verificados */}
  <StatCard 
    isDark={isDarkMode} 
    icon={ShieldCheck} 
    value={stats?.total_filled || 0} 
    label="Verificados" 
    color="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white" 
  />
</div>

        {/* FEED PRINCIPAL */}
        <div className="bg-white dark:bg-slate-900/40 border border-slate-200 dark:border-slate-800/60 rounded-[2.5rem] overflow-hidden shadow-2xl backdrop-blur-sm">
          <div className="p-8 border-b border-slate-100 dark:border-slate-800/60 flex flex-col md:flex-row justify-between items-center gap-4 bg-slate-50/50 dark:bg-slate-900/50">
            <div>
                <h2 className="text-2xl font-black text-slate-800 dark:text-white flex items-center gap-3">
                    <LayoutDashboard className="w-7 h-7 text-teal-500" /> Console de Gestão
                </h2>
                <p className="text-sm text-slate-500 font-medium mt-1">Gerencie a visibilidade de {jobs.length} itens no portal</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
                <Button variant="outline" onClick={loadData} className="rounded-xl border-slate-200 dark:border-slate-700 hover:bg-teal-50 dark:hover:bg-teal-900/20 flex-1 md:flex-none">
                    <RefreshCcw className="w-4 h-4 mr-2" /> Atualizar Fluxo
                </Button>
                <Button variant="outline" className="rounded-xl border-slate-200 dark:border-slate-700 flex-1 md:flex-none">
                    <Filter className="w-4 h-4 mr-2" /> Filtros
                </Button>
            </div>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
            {filteredJobs.length > 0 ? filteredJobs.map(job => (
              <div key={job.id} className="p-6 hover:bg-slate-50/80 dark:hover:bg-slate-800/30 transition-all group relative">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                  <div className="flex gap-6 items-start flex-1">
                    <div className="w-20 h-20 rounded-[1.5rem] bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0 border-2 border-white dark:border-slate-700 shadow-lg group-hover:scale-105 transition-transform duration-300">
                        {job.image_url ? (
                            <img src={MEDIA(job.image_url)} className="w-full h-full object-cover" alt="Thumb" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                              <ImageIcon className="w-8 h-8 opacity-20" />
                            </div>
                        )}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400 font-black text-[9px] uppercase tracking-tighter rounded-lg px-2 py-0.5">
                          {CATEGORIES.find(c => c.id === job.category)?.label || 'Vaga'}
                        </Badge>
                        {job.video_url && <Badge className="bg-blue-500 hover:bg-blue-500 rounded-lg h-5 px-1.5"><Video className="w-3 h-3 text-white" /></Badge>}
                        {job.document_url && <Badge className="bg-orange-500 hover:bg-orange-500 rounded-lg h-5 px-1.5"><FileUp className="w-3 h-3 text-white" /></Badge>}
                        <span className="text-[10px] text-slate-400 font-bold flex items-center gap-1 ml-2"><Clock className="w-3 h-3" /> Postado recentemente</span>
                      </div>
                      <h3 className="text-xl font-bold text-slate-800 dark:text-white group-hover:text-teal-500 transition-colors leading-snug">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-2">
                        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400"><Building2 className="w-3.5 h-3.5 text-teal-500" /> {job.company_name}</span>
                        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400"><MapPin className="w-3.5 h-3.5 text-rose-500" /> {job.city}</span>
                        <span className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400"><Eye className="w-3.5 h-3.5" /> {job.views || 0} acessos</span>
                        
                        <button 
                            onClick={() => {
                                navigator.clipboard.writeText(`https://vagas.empregamaisro.com.br/vagas/${job.slug || job.id}`);
                                toast.success('Link copiado para área de transferência!');
                            }}
                            className="text-[10px] font-black uppercase text-slate-400 hover:text-teal-500 flex items-center gap-1 transition-colors"
                        >
                            <Link2 className="w-3 h-3" /> Copiar Link
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end lg:self-center">
                    <Button variant="ghost" size="icon" onClick={() => handleOpenEditModal(job)} className="h-12 w-12 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10 rounded-2xl transition-all">
                        <Edit className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteJob(job.id)} className="h-12 w-12 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-2xl transition-all">
                        <Trash2 className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-12 w-12 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-2xl">
                        <ChevronRight className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            )) : (
              <div className="py-32 text-center">
                  <div className="bg-slate-100 dark:bg-slate-800/50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                      <Search className="w-10 h-10 text-slate-300" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 dark:text-white">Nenhum registro encontrado</h3>
                  <p className="text-slate-500 mt-2 max-w-xs mx-auto text-sm">Tente ajustar seus termos de pesquisa ou adicione um novo conteúdo clicando no botão superior.</p>
                  <Button onClick={handleOpenCreateModal} variant="link" className="text-teal-500 font-bold mt-4">Criar meu primeiro post</Button>
              </div>
            )}
          </div>
          
          <div className="p-6 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-800/60 flex justify-between items-center">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Mostrando {filteredJobs.length} de {jobs.length} registros ativos</p>
            <div className="flex gap-1">
                <Button disabled variant="outline" size="sm" className="rounded-lg h-8 w-8 p-0">1</Button>
            </div>
          </div>
        </div>
      </main>




      {/* --- DIALOG: EDITOR PRINCIPAL (FULL SCREEN) --- */}
      <Dialog open={showJobModal} onOpenChange={setShowJobModal}>
        <DialogContent className="max-w-[100vw] w-full h-[100vh] p-0 bg-[#F8FAFC] dark:bg-[#020617] border-none flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-300">
          
          {/* HEADER DO MODAL */}
          <div className="flex items-center justify-between px-10 py-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shrink-0 shadow-sm z-10">
            <div className="flex items-center gap-6">
                <div className="bg-gradient-to-br from-teal-500 to-teal-700 text-white p-4 rounded-3xl shadow-xl shadow-teal-500/30">
                    {isEditing ? <Edit className="w-7 h-7" /> : <Plus className="w-7 h-7" />}
                </div>
                <div>
                  <DialogTitle className="text-3xl font-black text-slate-900 dark:text-white font-heading tracking-tight leading-none">
                      {isEditing ? 'Refinar Publicação' : 'Criar Nova Publicação'}
                  </DialogTitle>
                  <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm font-bold mt-2 uppercase tracking-widest flex items-center gap-2">
                    <Activity className="w-4 h-4 text-teal-500" /> Ambiente de edição em tempo real
                  </DialogDescription>
                </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button type="button" variant="ghost" onClick={() => setShowJobModal(false)} className="text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 px-10 rounded-2xl h-14 font-black uppercase text-xs tracking-[0.2em] transition-all">Descartar</Button>
              <Button onClick={handleSaveJob} className="bg-teal-600 hover:bg-teal-500 text-white min-w-[240px] h-16 rounded-[1.5rem] shadow-2xl shadow-teal-500/40 font-black text-lg uppercase tracking-tight group transition-all active:scale-95">
                <CheckCircle className="w-6 h-6 mr-3 group-hover:scale-125 transition-transform" /> {isEditing ? 'Salvar Alterações' : 'Publicar no Portal'}
              </Button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar p-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 max-w-screen-2xl mx-auto">
                
                {/* COLUNA DA ESQUERDA: CONTEÚDO */}
                <div className="lg:col-span-8 space-y-10">
                    
                    {/* SEÇÃO: CABEÇALHO DO POST */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-10 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl relative overflow-hidden group">
                        <div className="absolute top-0 right-0 p-6 opacity-[0.03] group-hover:opacity-[0.07] transition-opacity"><LayoutDashboard size={200} /></div>
                        
                        <div className="space-y-3 relative">
                            <Label className={modernLabelClasses}>Título da Publicação <span className="text-teal-500">*</span></Label>
                            <Input
                                value={jobForm.title}
                                onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                                placeholder="Ex: Desenvolvedor Fullstack Sênior"
                                className={modernInputClasses + " text-2xl font-black py-8 border-teal-500/20 focus:border-teal-500"}
                            />
                        </div>

                        <div className="space-y-3 relative">
                            <Label className={modernLabelClasses}>
                                {isInfoType ? "Fonte ou Órgão Responsável" : "Empresa / Contratante"} <span className="text-teal-500">*</span>
                            </Label>
                            <Input
                                value={jobForm.company_name}
                                onChange={(e) => setJobForm({ ...jobForm, company_name: e.target.value })}
                                placeholder={isInfoType ? "Ex: Diário Oficial / G1" : "Nome da organização"}
                                className={modernInputClasses + " text-xl font-bold py-8"}
                            />
                        </div>
                    </div>
                    

                    {/* SEÇÃO: SEO E IDENTIDADE VISUAL */}
                    <div className="p-10 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-xl">
                        <div className="flex items-center gap-3 mb-6">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                            <h3 className="font-black text-slate-800 dark:text-white uppercase tracking-widest text-sm">Otimização de Link (Slug SEO)</h3>
                        </div>
                        <div className="flex items-center group">
                          <div className="bg-slate-50 dark:bg-slate-800 px-6 py-4 rounded-l-2xl border border-r-0 border-slate-200 dark:border-slate-700 text-slate-400 font-mono text-sm">
    https://www.empregamaisro.com.br/vagas/
</div>
                            <input 
                                type="text"
                                value={jobForm.slug}
                                onChange={(e) => setJobForm({...jobForm, slug: generateSlug(e.target.value)})}
                                placeholder="url-amigavel-do-post"
                                className={modernInputClasses + " rounded-l-none font-mono text-sm border-teal-500/10 focus:border-teal-500"}
                            />
                        </div>
                        <p className="text-[10px] text-slate-400 mt-4 font-bold italic uppercase tracking-[0.2em] pl-2">• Deixe em branco para gerar automaticamente com base no título</p>
                    </div>

                    {/* SEÇÃO: EDITOR RICH TEXT */}
                    <div className="flex flex-col min-h-[700px] rounded-[2.5rem] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl overflow-hidden group">
                        <div className="bg-slate-50 dark:bg-slate-800/80 p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-teal-600" />
                                </div>
                                <span className="font-black text-slate-800 dark:text-slate-100 uppercase tracking-[0.2em] text-xs">Corpo Detalhado do Conteúdo</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-3 py-1 bg-white dark:bg-slate-700 rounded-full border border-slate-100 dark:border-slate-600 shadow-sm">Suporte a HTML5 e Embeds</span>
                            </div>
                        </div>
                        <div className="flex-1 [&_.ql-container]:min-h-[600px] [&_.ql-editor]:p-10 [&_.ql-editor]:text-xl [&_.ql-toolbar]:bg-slate-50/50 dark:[&_.ql-toolbar]:bg-slate-800/50 [&_.ql-toolbar]:border-none dark:[&_.ql-container]:border-none dark:[&_.ql-editor]:text-slate-300">
                            <ReactQuill 
                                theme="snow"
                                value={jobForm.description}
                                onChange={(content) => setJobForm({ ...jobForm, description: content })}
                                modules={quillModules}
                                placeholder="Descreva aqui todas as informações, detalhes da vaga ou notícia de forma completa..."
                                className="h-full"
                            />
                        </div>
                    </div>
                </div>

                {/* COLUNA DA DIREITA: CONFIGURAÇÕES LATERAIS */}
                <div className="lg:col-span-4 space-y-8">
                    
                    {/* BLOCO: CATEGORIZAÇÃO */}
                    <div className={sidebarCardClasses}>
                        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-5">
                            <h3 className="text-slate-800 dark:text-white font-black flex items-center gap-2 text-xs uppercase tracking-[0.2em]">
                                <Tag className="w-4 h-4 text-teal-500" /> Classificação
                            </h3>
                            <Badge className="bg-teal-500">Obrigatório</Badge>
                        </div>
                        <div className="pt-2">
                            <Label className={modernLabelClasses}>Tipo de Conteúdo</Label>
                            <Select value={jobForm.category} onValueChange={(v) => setJobForm({ ...jobForm, category: v })}>
                                <SelectTrigger className={modernInputClasses + " h-16 border-none bg-slate-50 dark:bg-slate-800/50"}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-slate-200 dark:border-slate-800">
                                    {CATEGORIES.map(cat => (
                                        <SelectItem key={cat.id} value={cat.id} className="font-bold py-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-2 h-2 rounded-full bg-${cat.color}-500`} />
                                                {cat.label}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                   {/* BLOCO: CAPA E EDITOR DE IMAGEM */}
                    <div className={sidebarCardClasses}>
                        <h3 className="text-slate-800 dark:text-white font-black flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-5 text-xs uppercase tracking-[0.2em]">
                            <ImageIcon className="w-4 h-4 text-teal-500" /> Imagem de Destaque
                        </h3>
                        <div className="space-y-5">
                            <div className="grid grid-cols-3 gap-2 mt-3">
                                {['1:1', '16:9', '4:3'].map((ratio) => (
                                    <Button
                                        key={ratio}
                                        type="button"
                                        variant={imageRatio === ratio ? 'default' : 'outline'}
                                        onClick={() => setImageRatio(ratio)}
                                        className="h-10 text-[10px] font-black uppercase rounded-xl"
                                    >
                                        {ratio}
                                    </Button>
                                ))}
                            </div>

                            <div className="relative group overflow-hidden rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 min-h-[220px] flex flex-col items-center justify-center transition-all hover:border-teal-500/50">
                                {jobForm.image_url ? (
                                    <div className="relative w-full h-full min-h-[220px]">
                                        <img 
                                            src={MEDIA(jobForm.image_url)} 
                                            alt="Preview" 
                                            style={{
                                                objectFit: jobForm.image_display_mode,
                                                aspectRatio: imageRatio.replace(':', '/')
                                            }}
                                            className="w-full max-h-[220px]" 
                                        />

                                        <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-3 transition-all duration-300 backdrop-blur-sm">
                                            <Button 
                                                type="button"
                                                size="icon" 
                                                onClick={() => { setImageToEdit(jobForm.image_url); setShowAdvancedEditor(true); }} 
                                                className="rounded-2xl w-12 h-12 bg-teal-500 hover:bg-teal-400 text-white shadow-xl hover:scale-110 transition-all"
                                            >
                                                <Sparkles className="w-5 h-5" />
                                            </Button>

                                            <Button type="button" size="icon" variant="secondary" onClick={() => setShowImageEditor(true)} className="rounded-2xl w-12 h-12 shadow-xl hover:scale-110">
                                                <Scissors className="w-5 h-5" />
                                            </Button>

                                            <label className="cursor-pointer bg-white text-slate-900 w-12 h-12 rounded-2xl flex items-center justify-center hover:bg-teal-500 hover:text-white transition-all shadow-xl hover:scale-110">
                                                <Upload className="w-5 h-5" />
                                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                            </label>
                                        </div>
                                    </div>
                                ) : (
                                    <label className="w-full h-full min-h-[220px] flex flex-col items-center justify-center cursor-pointer hover:bg-teal-50/10 transition-all">
                                        <div className="bg-white dark:bg-slate-800 p-5 rounded-3xl shadow-xl mb-4 group-hover:scale-110 transition-transform">
                                            <Upload className="w-8 h-8 text-teal-500" />
                                        </div>
                                        <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Enviar Banner</span>
                                        <p className="text-[10px] text-slate-400 mt-2 font-bold italic">PNG, JPG ou WEBP (Max 5MB)</p>
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                    </label>
                                )}
                            </div>

                            {jobForm.image_url && (
                                <div className="flex gap-2">
                                    <Button type="button" variant="outline" onClick={() => setShowImageEditor(true)} className="flex-1 h-12 border-slate-200 dark:border-slate-700 text-xs font-black uppercase rounded-xl tracking-tighter">
                                        <SlidersHorizontal className="w-4 h-4 mr-2 text-teal-500" /> Ajustar Filtros
                                    </Button>
                                    <Button type="button" variant="ghost" onClick={() => setJobForm({...jobForm, image_url: ''})} className="text-rose-500 w-12 h-12 hover:bg-rose-50 rounded-xl border border-rose-100 dark:border-rose-900/30">
                                        <Trash2 className="w-5 h-5" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* BLOCO: MULTIMÍDIA E DOCUMENTAÇÃO */}
                    <div className={sidebarCardClasses}>
                        <h3 className="text-slate-800 dark:text-white font-black flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-5 text-xs uppercase tracking-[0.2em]">
                            <Video className="w-4 h-4 text-teal-500" /> Recursos Extras
                        </h3>
                        <div className="space-y-6">
                            <div>
                                <Label className={modernLabelClasses}>Vídeo Demonstrativo (URL)</Label>
                                <div className="flex gap-2">
                                    <Input value={jobForm.video_url} onChange={(e) => setJobForm({...jobForm, video_url: e.target.value})} placeholder="Youtube ou Direct MP4" className={modernInputClasses + " h-14 text-sm bg-slate-50 dark:bg-slate-800/50 border-none"} />
                                    <label className="shrink-0 w-14 h-14 rounded-xl flex items-center justify-center cursor-pointer bg-slate-100 dark:bg-slate-800 hover:bg-teal-500 hover:text-white transition-all shadow-sm">
                                        <Video className="w-5 h-5" />
                                        <input type="file" accept="video/*" className="hidden" onChange={(e) => handleFileUpload(e, 'video_url')} />
                                    </label>
                                </div>
                            </div>
                            <div>
                                <Label className={modernLabelClasses}>Documento / Edital (PDF)</Label>
                                <label className={`flex items-center gap-4 w-full p-5 rounded-2xl cursor-pointer border-2 border-dashed transition-all duration-300 ${jobForm.document_url ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-500/50' : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 hover:border-teal-500'}`}>
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${jobForm.document_url ? 'bg-emerald-500 text-white' : 'bg-white dark:bg-slate-700 text-slate-400'}`}>
                                        <FileUp className="w-6 h-6" />
                                    </div>
                                    <div className="flex-1 overflow-hidden">
                                        <p className="text-[10px] font-black uppercase tracking-widest truncate">{jobForm.document_url ? "PDF Vinculado" : "Anexar Documento"}</p>
                                        <p className="text-[10px] text-slate-400 truncate font-mono">{jobForm.document_url ? jobForm.document_url.split('/').pop() : "Máximo de 15MB por arquivo"}</p>
                                    </div>
                                    <input type="file" accept=".pdf" className="hidden" onChange={(e) => handleFileUpload(e, 'document_url')} />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* BLOCO: LOCALIZAÇÃO E REMUNERAÇÃO */}
                    <div className={sidebarCardClasses}>
                        <h3 className="text-slate-800 dark:text-white font-black flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-5 text-xs uppercase tracking-[0.2em]">
                            <Globe className="w-4 h-4 text-teal-500" /> Atributos Técnicos
                        </h3>
                        <div className="space-y-6 pt-2">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className={modernLabelClasses}>Cidade</Label>
                                    <Select value={jobForm.city} onValueChange={(v) => setJobForm({ ...jobForm, city: v })}>
                                        <SelectTrigger className="h-14 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-none focus:ring-2 focus:ring-teal-500/20"><SelectValue /></SelectTrigger>
                                        <SelectContent>{CITIES.map(c => <SelectItem key={c} value={c} className="font-bold">{c}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className={modernLabelClasses}>Segmento</Label>
                                    <Select value={jobForm.area} onValueChange={(v) => setJobForm({ ...jobForm, area: v })}>
                                        <SelectTrigger className="h-14 rounded-xl bg-slate-50 dark:bg-slate-800/50 border-none focus:ring-2 focus:ring-teal-500/20"><SelectValue /></SelectTrigger>
                                        <SelectContent>{AREAS.map(a => <SelectItem key={a} value={a} className="font-bold">{a}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                            </div>
                            
                            {!isInfoType && (
                                <div className="p-6 bg-blue-50/30 dark:bg-blue-900/10 rounded-[2rem] border border-blue-100 dark:border-blue-900/30 space-y-4">
                                    <Label className={modernLabelClasses + " text-blue-600 dark:text-blue-400"}>Modelo de Remuneração</Label>
                                    <Select value={salaryType} onValueChange={setSalaryType}>
                                        <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-slate-800 border-none shadow-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="fixed" className="font-bold text-xs">Salário Mensal Fixo</SelectItem>
                                            <SelectItem value="range" className="font-bold text-xs">Faixa Salarial (Min/Max)</SelectItem>
                                            <SelectItem value="negotiable" className="font-bold text-xs">A Combinar / Confidencial</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    
                                    {(salaryType === 'fixed' || salaryType === 'range') && (
                                        <div className="flex gap-2 animate-in slide-in-from-top-2 duration-300">
                                            <div className="relative flex-1">
                                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                <Input type="number" value={jobForm.salary_min} onChange={(e) => setJobForm({ ...jobForm, salary_min: e.target.value })} className="h-12 pl-10 rounded-xl bg-white dark:bg-slate-800 border-none text-sm font-bold" placeholder="Mínimo" />
                                            </div>
                                            {salaryType === 'range' && (
                                                <div className="relative flex-1">
                                                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                                    <Input type="number" value={jobForm.salary_max} onChange={(e) => setJobForm({ ...jobForm, salary_max: e.target.value })} className="h-12 pl-10 rounded-xl bg-white dark:bg-slate-800 border-none text-sm font-bold" placeholder="Máximo" />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* BLOCO: LINKS DE AÇÃO (CTA) */}
                    <div className={`${sidebarCardClasses} bg-slate-900 dark:bg-teal-900/20 text-white shadow-2xl shadow-slate-900/20 border-none overflow-hidden relative group`}>
                        <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:rotate-12 transition-transform duration-500"><LinkIcon size={80} /></div>
                        <h3 className="font-black flex items-center gap-2 border-b border-white/10 pb-5 text-xs uppercase tracking-[0.2em] relative z-10">
                            <Sparkles className="w-4 h-4 text-teal-400" /> Chamada para Ação
                        </h3>
                        <div className="space-y-6 pt-4 relative z-10">
                            <div>
                                <Label className="text-[10px] font-black text-white/60 uppercase mb-2 block tracking-widest">Link de Inscrição / Notícia Externa</Label>
                                <Input value={jobForm.link} onChange={(e) => setJobForm({ ...jobForm, link: e.target.value })} placeholder="https://portal-externo.com/vaga" className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-14 rounded-2xl focus:ring-teal-500 focus:bg-white/10 transition-all font-mono text-xs" />
                            </div>
                            <div>
                                <Label className="text-[10px] font-black text-white/60 uppercase mb-2 block tracking-widest">WhatsApp Direto (Contato)</Label>
                                <div className="relative">
                                    <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-400" />
                                    <Input value={jobForm.contact_phone} onChange={(e) => setJobForm({ ...jobForm, contact_phone: e.target.value })} placeholder="69999123456" className="bg-white/5 border-white/10 text-white placeholder:text-white/20 h-14 pl-12 rounded-2xl focus:ring-teal-500 focus:bg-white/10 transition-all font-mono text-xs" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- SUB-DIALOG: EDITOR DE IMAGEM AVANÇADO --- */}
      <Dialog open={showImageEditor} onOpenChange={setShowImageEditor}>
        <DialogContent className="max-w-4xl bg-white dark:bg-slate-950 p-0 rounded-[2.5rem] overflow-hidden border-none shadow-2xl">
          <div className="flex flex-col md:flex-row h-full">
            {/* PREVIEW */}
            <div className="flex-[1.5] bg-slate-100 dark:bg-slate-900 p-10 flex items-center justify-center border-r border-slate-200 dark:border-slate-800">
                <div className="relative w-full aspect-video rounded-3xl overflow-hidden shadow-2xl bg-white dark:bg-slate-800 flex items-center justify-center">
                    <img src={MEDIA(jobForm.image_url)} style={getImageStyle()} className="max-h-full max-w-full" alt="Edit Preview" />
                    <div className="absolute top-4 right-4 flex gap-2">
                        <Badge className="bg-black/50 backdrop-blur-md text-[10px]">Preview Live</Badge>
                    </div>
                </div>
            </div>
            
            {/* CONTROLES */}
            <div className="flex-1 p-10 flex flex-col justify-between">
                <div>
                    <h3 className="text-xl font-black text-slate-800 dark:text-white uppercase tracking-tighter flex items-center gap-2 mb-8">
                        <SlidersHorizontal className="w-5 h-5 text-teal-500" /> Processamento de Imagem
                    </h3>
                    
                    <div className="space-y-8">
                        {[
                          { key: 'brightness', label: 'Brilho', icon: Sun },
                          { key: 'contrast', label: 'Contraste', icon: Maximize },
                          { key: 'saturate', label: 'Saturação', icon: Sparkles },
                          { key: 'sepia', label: 'Sépia / Nostalgia', icon: Info },
                          { key: 'grayscale', label: 'P&B / Elegante', icon: Moon },
                          { key: 'rotate', label: 'Girar Ângulo', icon: RotateCw, min: 0, max: 360, unit: '°' },
                        ].map((f) => (
                          <div key={f.key} className="space-y-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase flex items-center gap-2 tracking-[0.1em]">
                                    <f.icon className="w-3 h-3 text-teal-500" /> {f.label}
                                </Label>
                                <span className="text-[10px] font-mono font-black text-teal-600 bg-teal-50 dark:bg-teal-900/30 px-2 py-0.5 rounded-md">{imageFilters[f.key]}{f.unit || '%'}</span>
                            </div>
                            <input 
                              type="range" 
                              min={f.min || 0} 
                              max={f.max || 200} 
                              value={imageFilters[f.key]}
                              onChange={(e) => setImageFilters({...imageFilters, [f.key]: parseInt(e.target.value)})}
                              className="w-full h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full appearance-none cursor-pointer accent-teal-500 transition-all hover:accent-teal-400"
                            />
                          </div>
                        ))}
                    </div>
                </div>

                <div className="pt-10 space-y-3">
                    <Button onClick={() => setShowImageEditor(false)} className="w-full h-14 bg-teal-600 hover:bg-teal-500 text-white rounded-2xl font-black uppercase text-xs tracking-widest shadow-xl shadow-teal-500/20 transition-all active:scale-95">Aplicar Processamento</Button>
                    <Button variant="ghost" className="w-full text-[10px] font-bold uppercase text-slate-400" onClick={() => setImageFilters({brightness:100, contrast:100, saturate:100, grayscale:0, rotate:0, sepia:0})}>Resetar Filtros de Fábrica</Button>
                </div>
            </div>
          </div>
       </DialogContent>
      </Dialog>

   {/* BLOCO: CAPA E EDITOR DE IMAGEM (UNIFICADO) */}
            <div className={sidebarCardClasses}>
                <h3 className="text-slate-800 dark:text-white font-black flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-5 text-xs uppercase tracking-[0.2em]">
                    <ImageIcon className="w-4 h-4 text-teal-500" /> Imagem de Destaque
                </h3>
                
                <div className="space-y-5">
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        {['1:1', '16:9', '4:3'].map((ratio) => (
                            <Button
                                key={ratio}
                                type="button"
                                variant={imageRatio === ratio ? 'default' : 'outline'}
                                onClick={() => setImageRatio(ratio)}
                                className="h-10 text-[10px] font-black uppercase rounded-xl"
                            >
                                {ratio}
                            </Button>
                        ))}
                    </div>

                    <div className="relative group overflow-hidden rounded-[2rem] border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 min-h-[220px] flex flex-col items-center justify-center transition-all">
                        {jobForm.image_url ? (
                            <div className="relative w-full h-full min-h-[220px]">
                                <img 
                                    src={MEDIA(jobForm.image_url)} 
                                    alt="Preview" 
                                    style={{
                                        objectFit: jobForm.image_display_mode,
                                        aspectRatio: imageRatio.replace(':', '/')
                                    }}
                                    className="w-full max-h-[220px]" 
                                />
                                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-3 transition-all duration-300">
                                    <Button 
                                        type="button" 
                                        size="icon" 
                                        onClick={() => { setImageToEdit(jobForm.image_url); setShowAdvancedEditor(true); }}
                                        className="bg-teal-500 hover:bg-teal-400 rounded-2xl"
                                    >
                                        <Sparkles className="w-5 h-5" />
                                    </Button>
                                    <label className="cursor-pointer bg-white text-slate-900 w-10 h-10 rounded-2xl flex items-center justify-center">
                                        <Upload className="w-5 h-5" />
                                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                    </label>
                                </div>
                            </div>
                        ) : (
                            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer p-6">
                                <Upload className="w-8 h-8 text-teal-500 mb-2" />
                                <span className="text-[10px] font-black uppercase text-slate-500">Enviar Banner</span>
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </label>
                        )}
                    </div>
                </div> 
            </div>


            {/* EDITOR AVANÇADO (TELA CHEIA - DESIGN ROBUSTO) */}
            {showAdvancedEditor && (
                <div className="fixed inset-0 z-[99999] bg-black overflow-hidden pointer-events-auto">
                    <ProfessionalPixelStudio 
                        initialImage={imageToEdit} 
                        onClose={() => setShowAdvancedEditor(false)}
                       onSave={async (editedImage) => {
    try {
        const loadingToast = toast.loading("Otimizando Renderização...");
        const optimizedBlob = await getOptimizedBlob(editedImage);
        const fileToUpload = new File([optimizedBlob], 'capa.jpg', { type: 'image/jpeg' });
        
        const uploadRes = await uploadAPI.upload(fileToUpload);
        const rawUrl = uploadRes.data?.url || uploadRes.url || uploadRes; 
        
        // ✅ TRATAMENTO PARA URL ABSOLUTA (ESSENCIAL PARA WHATSAPP)
        const finalUrl = rawUrl.startsWith('http') 
            ? rawUrl 
            : `${BACKEND_URL}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
        
        setJobForm(prev => ({ ...prev, image_url: finalUrl }));
        
        setShowAdvancedEditor(false);
        toast.dismiss(loadingToast);
        toast.success("Imagem pronta para o WhatsApp!");
    } catch (error) {
        console.error("Erro no Pixel Studio:", error);
        toast.dismiss();
        toast.error("Falha ao processar mídia.");
    }
}}
                    />
                </div>
            )}
        </div> 
       
    ); 
}; 

export default AdminDashboardPage;